/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { Handler, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { Cart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import validateAddress, { AddressErrors } from "@insite/client-framework/Store/CommonHandlers/ValidateAddress";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    chain,
    DispatchSetInitialValues,
    SetCart,
    ValidateBillingAddress,
    ValidateShippingAddress,
} from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import { BillToModel, CountryModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = Handler<
    {},
    {
        cart: Cart;
        billTo: BillToModel;
        shipTo: ShipToModel;
        country: CountryModel | undefined;
        shipTos: ShipToModel[];
    }
>;

export const xDispatchSetInitialValues: HandlerType = props => {
    // this to override validations
    const state = props.getState();

    const BTCompanyNameIsRequired = props.billTo.validation!.companyName!.isRequired;

    const billTo: any = {
        ...props.billTo,
        validation: {
            ...props.billTo.validation,
            companyName: { ...props.billTo.validation?.companyName, isRequired: BTCompanyNameIsRequired },
        },
    };

    const STCompanyNameIsRequired = props.shipTo.validation!.companyName!.isRequired;

    const shipTo: any = {
        ...props.shipTo,
        validation: {
            ...props.shipTo.validation,
            companyName: { ...props.shipTo.validation?.companyName, isRequired: STCompanyNameIsRequired },
        },
    };

    props.dispatch({
        type: "Pages/CheckoutShipping/SetInitialValues",
        cart: props.cart,
        shipTo,
        billTo,
        country: props.country,
    });
};

// clearing the order lines from zero-priced items
export const XngSetCart: HandlerType = props => {
    const state = props.getState();
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    if (!cart) {
        throw new Error("There was no current cart and we are trying to use it to set initial values");
    }
    let cleanedCartWithoutZeroPricedLines = {} as Cart;
    if (cart && cart.cartLines) {
        cleanedCartWithoutZeroPricedLines = {
            ...cart,
            cartLines: cart.cartLines.filter(line => line?.pricing?.unitNetPrice !== 0 || line.isPromotionItem),
        };
    }
    props.cart = cleanedCartWithoutZeroPricedLines;
};

export const xValidateBillingAddress: HandlerType = async ({ dispatch, getState, billTo }) => {
    const state = getState();
    const {
        pages: {
            checkoutShipping: { billingAddressFormState },
        },
    } = state;
    if (!billingAddressFormState) {
        return;
    }

    const addressFieldsDataView = getAddressFieldsDataView(state);
    if (!addressFieldsDataView.value) {
        throw new Error("There were no address fields loaded yet");
    }

    const awaitableValidate = makeHandlerChainAwaitable<Parameters<typeof validateAddress>[0], AddressErrors>(
        validateAddress,
    );
    const billingAddressErrors = await awaitableValidate({
        address: billTo,
        validationRules: billTo.validation!,
        addressFieldDisplayCollection: addressFieldsDataView.value.billToAddressFields,
    })(dispatch, getState);
    const isBillingAddressValid = Object.keys(billingAddressErrors).length === 0;

    const isB2B = billTo.properties.isB2B === "True";
    const isRequestUpdate = isB2B;

    const isBillingAddressUpdateRequired = !isRequestUpdate && !isBillingAddressValid;

    dispatch({
        type: "Pages/CheckoutShipping/SetIsBillingAddressUpdateRequired",
        isBillingAddressUpdateRequired,
    });
};

export const xValidateShippingAddress: HandlerType = async ({ dispatch, getState, shipTo, billTo }) => {
    const state = getState();
    const {
        context: { session, settings },
    } = state;
    if (!shipTo || session.fulfillmentMethod === FulfillmentMethod.PickUp) {
        return;
    }

    const addressFieldsDataView = getAddressFieldsDataView(state);
    if (!addressFieldsDataView.value) {
        throw new Error("There were no address fields loaded yet");
    }

    const awaitableValidate = makeHandlerChainAwaitable<Parameters<typeof validateAddress>[0], AddressErrors>(
        validateAddress,
    );
    const shippingAddressErrors = await awaitableValidate({
        address: shipTo,
        validationRules: shipTo.validation!,
        addressFieldDisplayCollection: addressFieldsDataView.value.shipToAddressFields,
    })(dispatch, getState);
    const isShippingAddressValid = Object.keys(shippingAddressErrors).length === 0;

    const isAdmin = session.userRoles.includes("Administrator");
    const isB2B = billTo?.properties?.isB2B === "True";
    const { b2BCanManageShippingAddress } = settings.settingsCollection?.customSettings as any;
    const canManageShipping = b2BCanManageShippingAddress === "true";

    const allowEdit = isAdmin || canManageShipping;
    const isRequestUpdate = !allowEdit;

    const isShippingAddressUpdateRequired = !isRequestUpdate && !isShippingAddressValid;

    dispatch({
        type: "Pages/CheckoutShipping/SetIsShippingAddressUpdateRequired",
        isShippingAddressUpdateRequired,
    });
};

replaceInChain(chain, DispatchSetInitialValues, xDispatchSetInitialValues);
replaceInChain(chain, SetCart, XngSetCart);
replaceInChain(chain, ValidateBillingAddress, xValidateBillingAddress);
replaceInChain(chain, ValidateShippingAddress, xValidateShippingAddress);
